
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useUser } from "@/hooks/use-auth";
import ProfileReviews from "@/components/ProfileReviews";
import { fetchPublicProfile, fetchFullProfile } from "@/lib/api/profiles";
import { fetchOffersBySellerId } from "@/lib/api/offers";
import { PublicProfile } from "@/types/profiles";
import { UserInfoCard } from "@/components/profile/UserInfoCard";
import { UserActivityCard } from "@/components/profile/UserActivityCard";
import { UserOffersTab } from "@/components/profile/UserOffersTab";

const UserProfile = () => {
  const { userId } = useParams<{ userId: string }>();
  const currentUser = useUser();
  const isOwnProfile = currentUser?.id === userId;

  const { data: profile, isLoading, error } = useQuery<PublicProfile | null>({
    queryKey: ['public-profile', userId],
    queryFn: async () => {
      if (!userId) return null;
      try {
        if (isOwnProfile && currentUser) {
          return await fetchFullProfile(userId, currentUser.id);
        }
        return await fetchPublicProfile(userId);
      } catch (error) {
        console.error("Error fetching profile:", error);
        throw error;
      }
    },
    enabled: !!userId,
  });

  const { data: userOffers, isLoading: isOffersLoading } = useQuery({
    queryKey: ['user-offers', userId, profile?.seller_profile_id],
    queryFn: async () => {
      if (!userId || !profile?.seller_profile_id) return [];
      return fetchOffersBySellerId(profile.seller_profile_id);
    },
    enabled: !!profile?.seller_profile_id,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="container mx-auto py-10 text-center">
        <h2 className="text-2xl font-bold mb-4">User Not Found</h2>
        <p className="mb-4">The user profile you're looking for doesn't exist or has been removed.</p>
        {error && (
          <p className="text-muted-foreground mb-4 text-sm">Error details: {error.message || "Unknown error"}</p>
        )}
        <Button asChild>
          <Link to="/">Go Back Home</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-10">
      <div className="grid grid-cols-1 gap-6">
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="offers">Offers</TabsTrigger>
            <TabsTrigger value="reviews">Reviews</TabsTrigger>
          </TabsList>
          
          <TabsContent value="profile" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <UserInfoCard 
                profile={profile} 
                isOwnProfile={isOwnProfile}
                userOffers={userOffers}
              />
              <UserActivityCard userOffers={userOffers || []} />
            </div>
          </TabsContent>
          
          <TabsContent value="offers" className="mt-6">
            <UserOffersTab 
              isLoading={isOffersLoading}
              userOffers={userOffers}
            />
          </TabsContent>
          
          <TabsContent value="reviews" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>User Reviews</CardTitle>
              </CardHeader>
              <CardContent>
                <ProfileReviews 
                  profileId={userId || ""} 
                  limit={10}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default UserProfile;
